import IconCross from 'assets/icons/IconCross.svg'
import * as Common from 'components/common/Common'
import * as Typography from 'components/common/Typography'
import { useAppSelector } from 'hooks/useRedux'
import { ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'
import { selectFitCustomResolution } from 'redux/slices/app'
import { useOnClickOutside } from 'shared-ui'
import styled, { css } from 'styled-components'
import { ICommonModalProps } from 'types/common'

interface IModalProps extends ICommonModalProps {
  children: ReactNode
  className?: string
  closeOnClickOutside?: boolean
  withExitButton?: boolean
  title?: string
}

export const ModalBody = styled(Common.Div)(
  ({ theme: { spacing, colors, isKiosk, params } }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    margin-right: -50%;
    transform: translate(-50%, -50%);
    background: ${colors.modalBackground};
    padding: ${spacing(3)} ${spacing(5)};
    min-width: 50vw;
    min-height: 50vh;
    max-height: 90vh;
    border: none;
    border-radius: ${params.buttonBorderRadius};
    box-shadow: 0 0 35px rgba(0, 0, 0, 0.1);
    overflow: auto;

    &::-webkit-scrollbar {
      /* Hide the scrollbar */
      display: none;
    }

    ${isKiosk &&
    css`
      width: calc(100% - 96px);
      height: calc(100% - 124px);
      border-radius: ${params.buttonBorderRadius};
      top: 80px;
      left: 48px;
      transform: translate(0, 0);
    `}
  `
)

export const ModalOverlay = styled.div(
  ({ theme: { colors } }) => css`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${colors.modalOverlay};
  `
)

export const CloseButton = styled(Common.SvgIcon)(
  ({ theme: { spacing } }) => css`
    position: absolute;
    top: ${spacing(2)};
    right: ${spacing(3)};
  `
)

const Modal = ({
  isOpen,
  setIsOpen,
  children,
  className,
  closeOnClickOutside = true,
  withExitButton = true,
  title,
}: IModalProps) => {
  const isMobile = useAppSelector(state =>
    selectFitCustomResolution(state, 926)
  )

  const modalRef = useRef(null)

  if (closeOnClickOutside) {
    useOnClickOutside(modalRef, () => {
      setIsOpen(false)
    })
  }

  const handleCloseClick = () => {
    setIsOpen(false)
  }

  return isOpen
    ? createPortal(
        <>
          <ModalOverlay />
          <ModalBody
            ref={modalRef}
            className={className}
            isMobile={isMobile}
          >
            {(title || withExitButton) && (
              <Common.Div
                flex="row"
                justifyContent="space-between"
                mb={2}
              >
                {title && <Typography.H4 primary>{title}</Typography.H4>}
                {withExitButton && (
                  <CloseButton
                    src={IconCross}
                    onClick={handleCloseClick}
                  />
                )}
              </Common.Div>
            )}
            {children}
          </ModalBody>
        </>,
        document.body
      )
    : null
}

export default Modal
